"use client"

import { NodeViewWrapper, NodeViewProps } from "@tiptap/react"
import { useState, useCallback } from "react"
import {
  Plus,
  Pencil,
  X,
  BookOpen,
} from "lucide-react"
import { cn } from "@/lib/utils"
import type { SeriesItem } from "./SeriesCardExtension"
import { useResizable } from "./useResizable"
import { NodeToolbar, ResizeHandles, SizeIndicator } from "./NodeToolbar"

interface SeriesItemEditorProps {
  item: SeriesItem
  index: number
  onSave: (item: SeriesItem) => void
  onCancel: () => void
}

function SeriesItemEditor({ item, index, onSave, onCancel }: SeriesItemEditorProps) {
  const [editedItem, setEditedItem] = useState(item)

  return (
    <div className="p-3 bg-bg-alt border border-border rounded space-y-2">
      <div>
        <label className="text-xs text-text-muted block mb-1">Title</label>
        <input
          type="text"
          value={editedItem.title}
          onChange={(e) => setEditedItem({ ...editedItem, title: e.target.value })}
          className="w-full px-3 py-1.5 text-sm bg-bg-card border border-border rounded text-text"
          placeholder={`Part ${index + 1}`}
        />
      </div>
      <div>
        <label className="text-xs text-text-muted block mb-1">Link (slug)</label>
        <input
          type="text"
          value={editedItem.slug}
          onChange={(e) => setEditedItem({ ...editedItem, slug: e.target.value })}
          className="w-full px-3 py-1.5 text-sm bg-bg-card border border-border rounded text-text"
          placeholder="/blog/post-slug"
        />
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id={`current-${item.id}`}
          checked={editedItem.isCurrent}
          onChange={(e) => setEditedItem({ ...editedItem, isCurrent: e.target.checked })}
          className="rounded border-border bg-bg-card accent-primary"
        />
        <label htmlFor={`current-${item.id}`} className="text-xs text-text-muted">
          Current article
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-2 py-1 text-xs text-text-muted hover:text-text"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSave(editedItem)}
          className="px-2 py-1 text-xs bg-primary text-white rounded hover:bg-primary-hover"
        >
          Save
        </button>
      </div>
    </div>
  )
}

export function SeriesCardNodeView({
  node,
  updateAttributes,
  deleteNode,
  selected,
  editor,
  getPos,
}: NodeViewProps) {
  const { title, items, currentIndex, width } = node.attrs as {
    title: string
    items: SeriesItem[]
    currentIndex: number
    width: number | null
  }

  const [editingId, setEditingId] = useState<string | null>(null)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleText, setTitleText] = useState(title)

  const {
    containerRef,
    currentWidth,
    isResizing,
    resizeDirection,
    handleResizeStart,
    resetSize,
  } = useResizable({
    initialWidth: width,
    initialHeight: null,
    minWidth: 280,
    minHeight: 80,
    onWidthChange: useCallback(
      (newWidth: number | null) => updateAttributes({ width: newWidth }),
      [updateAttributes]
    ),
    onHeightChange: useCallback(() => {}, []),
  })

  const addItem = () => {
    const newItem: SeriesItem = {
      id: crypto.randomUUID(),
      title: `Part ${items.length + 1}`,
      slug: "#",
      isCurrent: false,
    }
    updateAttributes({ items: [...items, newItem] })
    setEditingId(newItem.id)
  }

  const updateItem = (updatedItem: SeriesItem) => {
    // If this item is marked as current, unmark others
    let updatedItems = items.map((item) => {
      if (item.id === updatedItem.id) return updatedItem
      if (updatedItem.isCurrent && item.isCurrent) {
        return { ...item, isCurrent: false }
      }
      return item
    })

    // Find new current index
    const newCurrentIndex = updatedItems.findIndex((i) => i.isCurrent)

    updateAttributes({
      items: updatedItems,
      currentIndex: newCurrentIndex >= 0 ? newCurrentIndex : currentIndex,
    })
    setEditingId(null)
  }

  const deleteItem = (id: string) => {
    const newItems = items.filter((item) => item.id !== id)
    const newCurrentIndex = newItems.findIndex((i) => i.isCurrent)
    updateAttributes({
      items: newItems,
      currentIndex: newCurrentIndex >= 0 ? newCurrentIndex : 0,
    })
  }

  const handleTitleSave = () => {
    updateAttributes({ title: titleText })
    setIsEditingTitle(false)
  }

  const handleSelectNode = () => {
    const pos = getPos()
    if (typeof pos === "number" && editor) {
      editor.chain().focus().setNodeSelection(pos).run()
    }
  }

  const completedCount = currentIndex + 1
  const totalCount = items.length
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0

  return (
    <NodeViewWrapper
      className="my-6 group relative"
      style={{ width: currentWidth ? `${currentWidth}px` : undefined }}
      ref={containerRef}
    >
      {/* Toolbar - positioned outside the ring container */}
      {selected && (
        <NodeToolbar
          onDelete={deleteNode}
          showResetSize={!!currentWidth}
          onResetSize={resetSize}
          position="top-right"
          className="-top-12 right-0"
        >
          <button
            type="button"
            onClick={addItem}
            className="flex items-center gap-1 px-2 py-1 text-xs text-text-secondary hover:text-text hover:bg-bg-alt rounded"
          >
            <Plus className="w-3 h-3" />
            Add Part
          </button>
          <div className="w-px h-5 bg-border" />
        </NodeToolbar>
      )}

      <div
        className={cn(
          "rounded-xl border border-border bg-bg-card overflow-hidden relative",
          selected && "ring-2 ring-primary ring-offset-2 ring-offset-bg-card",
          isResizing && "select-none"
        )}
      >
        {/* Resize handles */}
        <ResizeHandles
          show={selected}
          isResizing={isResizing}
          resizeDirection={resizeDirection}
          onResizeStart={handleResizeStart}
        />

        {/* Size indicator */}
        <SizeIndicator show={isResizing} width={currentWidth} height={null} />

        {/* Selection Header Bar */}
        <div
          className={cn(
            "flex items-center justify-between px-4 py-2",
            "bg-bg-alt border-b border-border",
            "cursor-pointer select-none",
            "hover:bg-bg transition-colors"
          )}
          contentEditable={false}
          onClick={handleSelectNode}
        >
          <div className="flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-primary" />
            <span className="text-sm text-text-muted">
              Series · {items.length} part{items.length !== 1 ? "s" : ""}
            </span>
          </div>
          <span className="text-xs text-text-muted">
            {selected ? "Click items to edit" : "Click to select"}
          </span>
        </div>

        {/* Card content */}
        <div className="p-6">
          {/* Header */}
          <div className="mb-4">
            <span className="text-xs font-semibold text-primary uppercase tracking-wider">
              SERIES
            </span>
            {isEditingTitle ? (
              <input
                type="text"
                value={titleText}
                onChange={(e) => setTitleText(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleTitleSave()
                  if (e.key === "Escape") {
                    setTitleText(title)
                    setIsEditingTitle(false)
                  }
                }}
                className="block w-full mt-1 text-xl font-bold bg-transparent border-b border-primary text-text outline-none"
                autoFocus
              />
            ) : (
              <h3
                onClick={() => setIsEditingTitle(true)}
                className="text-xl font-bold text-text mt-1 cursor-text hover:text-primary transition-colors"
                contentEditable={false}
              >
                {title}
              </h3>
            )}
          </div>

          {/* Items list */}
          <div className="space-y-2">
            {items.map((item, index) => {
              if (editingId === item.id) {
                return (
                  <div key={item.id} contentEditable={false}>
                    <SeriesItemEditor
                      item={item}
                      index={index}
                      onSave={updateItem}
                      onCancel={() => setEditingId(null)}
                    />
                  </div>
                )
              }

              return (
                <div
                  key={item.id}
                  className={cn(
                    "flex items-center gap-3 group/item",
                    "transition-colors"
                  )}
                  contentEditable={false}
                >
                  {/* Number badge */}
                  <span
                    className={cn(
                      "w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0",
                      item.isCurrent
                        ? "bg-primary text-white"
                        : index <= currentIndex
                        ? "bg-primary/20 text-primary"
                        : "bg-bg-alt text-text-muted"
                    )}
                  >
                    {index + 1}
                  </span>

                  {/* Title */}
                  <a
                    href={item.slug}
                    className={cn(
                      "flex-1 text-sm transition-colors",
                      item.isCurrent
                        ? "text-text font-medium"
                        : "text-text-muted hover:text-text"
                    )}
                  >
                    {item.title}
                  </a>

                  {/* Current badge */}
                  {item.isCurrent && (
                    <span className="text-xs text-primary">(Current)</span>
                  )}

                  {/* Edit/Delete buttons */}
                  {selected && (
                    <div className="flex gap-1 opacity-0 group-hover/item:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => setEditingId(item.id)}
                        className="p-1 rounded text-text-muted hover:text-text hover:bg-bg-alt"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteItem(item.id)}
                        className="p-1 rounded text-text-muted hover:text-red-600 hover:bg-red-50"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Progress */}
          <div className="mt-6 pt-4 border-t border-border">
            <div className="flex justify-between text-xs text-text-muted mb-2">
              <span>
                Part {completedCount} of {totalCount}
              </span>
              <span>{Math.round(progressPercent)}% Complete</span>
            </div>
            <div className="h-1.5 bg-bg-alt rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>

        {/* Empty state */}
        {items.length === 0 && (
          <div
            className="text-center py-4 text-text-muted border-t border-border"
            contentEditable={false}
          >
            <button
              type="button"
              onClick={addItem}
              className="px-4 py-2 text-sm text-primary hover:underline"
            >
              <Plus className="w-4 h-4 inline mr-1" />
              Add first part
            </button>
          </div>
        )}
      </div>
    </NodeViewWrapper>
  )
}
